import React, { useEffect, useState } from 'react';
import { useMetaTags } from './hooks/useMetaTags';
import './LandingPage.css';

const WHATSAPP_URL = 'https://wa.me/50361896548?text=Hola%20Florentina%20Garden%2C%20quiero%20consultar%20por%20plantas.';
const INSTAGRAM_URL = 'https://www.instagram.com/viveroflorentina11';
const FACEBOOK_URL = 'https://www.facebook.com/viveroflorentina11';
const MAPS_URL = 'https://www.google.com/maps/search/?api=1&query=Florentina+Garden+Atiquizaya';

const PLANT_TYPES = [
  { image: '/cactus-florido.jpg', title: 'Cactus', text: 'Variedades para cada rincón y nivel de cuidado.' },
  { image: '/suculenta-02.jpg', title: 'Suculentas', text: 'Pequeñas, resistentes y llenas de personalidad.' },
  { image: '/follaje.jpg', title: 'Follaje', text: 'Hojas llenas de color para transformar tus espacios.' },
  { image: '/suculenta-05.jpg', title: 'Para regalar', text: 'Detalles naturales para celebrar momentos especiales.' },
];

const STEPS = [
  ['01', 'Elegí tus favoritas', 'Escribinos por WhatsApp y te compartimos las plantas disponibles.'],
  ['02', 'Reservá tu pedido', 'Confirmamos existencias, precio y la mejor forma de entrega.'],
  ['03', 'Recibí tu jardín', 'Coordinamos ruta de entrega o te esperamos en el vivero.'],
];

const LandingPage: React.FC = () => {
  useMetaTags({
    title: 'Florentina Garden - Inicio',
    description: 'Florentina Garden: vivero en Atiquizaya, Ahuachapán. Cactus, suculentas y follaje con rutas de entrega y envíos a todo El Salvador.',
    url: window.location.href,
  });

  const [scrollY, setScrollY] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    let frameId = 0;
    const updateScroll = () => {
      frameId = 0;
      setScrollY(window.scrollY);
    };
    const handleScroll = () => {
      if (frameId === 0) frameId = window.requestAnimationFrame(updateScroll);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setReduceMotion(mediaQuery.matches);
    updatePreference();
    mediaQuery.addEventListener('change', updatePreference);
    return () => mediaQuery.removeEventListener('change', updatePreference);
  }, []);

  const heroOffset = reduceMotion ? 0 : Math.min(scrollY * 0.11, 75);
  const deliveryOffset = reduceMotion ? 0 : Math.max(0, (scrollY - 700) * 0.07);

  return (
  <main className="florentina-landing">
    <a className="skip-link" href="#contenido">Ir al contenido</a>

    <header className="site-header">
      <a className="brand" href="#inicio" aria-label="Florentina Garden, inicio">
        <img src="/florentina-garden-logo.png" alt="Florentina Garden" />
      </a>
      <nav className="site-nav" aria-label="Navegación principal">
        <a href="#plantas">Plantas</a>
        <a href="#envios">Envíos</a>
        <a href="#visitanos">Visítanos</a>
      </nav>
      <a className="header-cta" href={WHATSAPP_URL} target="_blank" rel="noreferrer">WhatsApp <span aria-hidden="true">↗</span></a>
    </header>

    <section className="hero-section" id="inicio" aria-labelledby="hero-title">
      <div className="hero-copy" id="contenido">
        <p className="eyebrow"><span>●</span> Vivero en Atiquizaya, El Salvador</p>
        <h1 id="hero-title">Cultivamos vida<br /><em>para tu espacio.</em></h1>
        <p className="hero-description">Cactus, suculentas y plantas ornamentales cultivadas con paciencia, listas para encontrar su nuevo hogar.</p>
        <div className="hero-actions">
          <a className="button button--solid" href={WHATSAPP_URL} target="_blank" rel="noreferrer">Consultar por WhatsApp <span aria-hidden="true">→</span></a>
          <a className="button button--text" href="#plantas">Explorar plantas <span aria-hidden="true">↓</span></a>
        </div>
        <div className="trust-row" aria-label="Información destacada">
          <div><strong>Envíos</strong><span>A todo El Salvador</span></div>
          <div><strong>Productores</strong><span>Salvadoreños</span></div>
          <div><strong>Atención</strong><span>Personalizada</span></div>
        </div>
      </div>
      <div className="hero-art" aria-hidden="true">
        <div className="sun-disc" style={{ transform: `translateY(${heroOffset * -0.55}px)` }} />
        <div className="hero-arch hero-arch--back" style={{ transform: `rotate(7deg) translateY(${heroOffset * 0.3}px)` }} />
        <div className="hero-arch hero-arch--front" style={{ transform: `translateY(${heroOffset}px)` }}>
          <img src="/hero-suculenta-edited.png" alt="" />
        </div>
        <p>cultivado con amor</p>
      </div>
    </section>

    <section className="plant-section" id="plantas" aria-labelledby="plantas-title">
      <div className="section-heading">
        <p className="eyebrow">Elegí tu favorita</p>
        <h2 id="plantas-title">Plantas para<br /><em>todos los días.</em></h2>
        <p>No trabajamos con un catálogo fijo: escribinos y te mostramos los ejemplares disponibles hoy.</p>
      </div>
      <div className="plant-grid">
        {PLANT_TYPES.map((plant) => (
          <article className="plant-card" key={plant.title}>
            <img className="plant-card__photo" src={plant.image} alt="" />
            <h3>{plant.title}</h3>
            <p>{plant.text}</p>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer">Consultar disponibilidad <span aria-hidden="true">→</span></a>
          </article>
        ))}
      </div>
    </section>

    <section className="delivery-section" id="envios" aria-labelledby="envios-title">
      <div className="delivery-illustration" aria-hidden="true">
        <span className="delivery-sun" style={{ transform: `translateY(${deliveryOffset * -0.6}px)` }} />
        <span className="delivery-road"><i /></span>
        <span className="delivery-route route--one" />
        <span className="delivery-route route--two" />
        <div className="delivery-truck" style={{ marginLeft: `${deliveryOffset}px` }}>
          <div className="truck-cargo"><span>Florentina</span><i>✦</i></div>
          <div className="truck-cab"><span /></div>
          <i className="truck-wheel truck-wheel--back" />
          <i className="truck-wheel truck-wheel--front" />
        </div>
        <span className="delivery-leaf delivery-leaf--one">✦</span>
        <span className="delivery-leaf delivery-leaf--two">✦</span>
      </div>
      <div className="delivery-copy">
        <p className="eyebrow"><span>●</span> Hasta tu puerta</p>
        <h2 id="envios-title">Tu pedido<br /><em>viaja seguro.</em></h2>
        <p>Contamos con rutas de entrega y realizamos envíos a todo El Salvador. Coordinamos contigo la opción más conveniente para que tus plantas lleguen bien.</p>
        <a className="button button--light" href={WHATSAPP_URL} target="_blank" rel="noreferrer">Consultar ruta de entrega <span aria-hidden="true">→</span></a>
      </div>
    </section>

    <section className="process-section" aria-labelledby="proceso-title">
      <div className="section-heading section-heading--center">
        <p className="eyebrow">Fácil y cercano</p>
        <h2 id="proceso-title">Así llega tu planta<br /><em>hasta vos.</em></h2>
      </div>
      <ol className="steps-list">
        {STEPS.map(([number, title, text]) => (
          <li key={number}>
            <span>{number}</span>
            <h3>{title}</h3>
            <p>{text}</p>
          </li>
        ))}
      </ol>
    </section>

    <section className="visit-section" id="visitanos" aria-labelledby="visita-title">
      <div>
        <p className="eyebrow">Conocé el vivero</p>
        <h2 id="visita-title">Un rincón verde<br /><em>en Atiquizaya.</em></h2>
        <p>Visitá Florentina Garden en Caserío Cruz Verde, Atiquizaya, Ahuachapán. Te ayudamos a elegir una planta que se adapte a tu espacio.</p>
        <div className="visit-actions">
          <a className="button button--solid" href={MAPS_URL} target="_blank" rel="noreferrer">Cómo llegar <span aria-hidden="true">↗</span></a>
          <a className="phone-link" href="tel:+50361896548">+503 6189 6548</a>
        </div>
      </div>
      <aside className="visit-card">
        <p className="visit-card__label">Florentina Garden</p>
        <p className="visit-card__address">Caserío Cruz Verde<br />Atiquizaya, Ahuachapán</p>
        <a href={MAPS_URL} target="_blank" rel="noreferrer">Abrir en Google Maps <span aria-hidden="true">↗</span></a>
      </aside>
    </section>

    <footer className="site-footer">
      <img className="footer-logo" src="/florentina-garden-logo.png" alt="Florentina Garden" />
      <p>Plantas cultivadas con amor, desde El Salvador.</p>
      <div className="footer-links">
        <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer">Instagram</a>
        <a href={FACEBOOK_URL} target="_blank" rel="noreferrer">Facebook</a>
        <a href={WHATSAPP_URL} target="_blank" rel="noreferrer">WhatsApp</a>
      </div>
      <small>© {new Date().getFullYear()} Florentina Garden</small>
    </footer>
  </main>
  );
};

export default LandingPage;
